import React from 'react';
import { Link, Typography } from '@mui/material';
import { noCase } from 'change-case';
import { Job, JobStatus } from 'src/@types/jobs';
import { useNavigate } from 'react-router';
import { Box, Stack } from '@mui/system';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { TourJobInfo } from './notifications';
import { useLocales } from '../../../locales';
import axios from '../../../utils/axios';
import { epochDaysToFormattedDate } from '../../../utils/timeAndDateHelper';
import { dispatch } from '../../../redux/store';
import { apiSlice } from '../../../redux/slices/apiSlice';
import { getListPathForType } from '../../../utils/urlHelper';

const graphHopperReasonTypes: Record<number, string> = {
  1: 'SKILL_CONSTRAINT',
  2: 'TIME_WINDOW_CONSTRAINT',
  3: 'CAPACITY_CONSTRAINT',
  4: 'MAX_DISTANCE_CONSTRAINT',
  21: 'RELATION_CONSTRAINT',
  22: 'ALLOWED_VEHICLE_CONSTRAINT',
  23: 'MAX_TIME_IN_VEHICLE_CONSTRAINT',
  24: 'BREAK_NOT_REQUIRED',
  25: 'DISALLOWED_VEHICLE_CONSTRAINT',
  26: 'MAX_DRIVE_TIME_CONSTRAINT',
  27: 'MAX_JOB_CONSTRAINT',
  28: 'MAX_ACTIVITY_CONSTRAINT',
  29: 'GROUP_RELATION_CONSTRAINT',
  30: 'DRIVING_TIME_BREAK_CONSTRAINT',
  50: 'LOCATION_NOT_ACCESSIBLE_CONSTRAINT',
  51: 'LOCATION_ISOLATED_CONSTRAINT',
};

const clickablePlanningResultSx = {
  display: 'inline-block',
  borderRadius: 0.5,
  px: 0.5,
  mx: -0.5,
  cursor: 'pointer',
  transition: 'background-color 150ms ease-in-out',
  '&:hover': {
    bgcolor: 'action.hover',
  },
} as const;

const parseUnassignedStudentReasons = (serializedReasons?: string): Record<string, number[]> => {
  const reasonsByStudent: Record<string, number[]> = {};
  if (!serializedReasons) {
    return reasonsByStudent;
  }

  serializedReasons.split(';').forEach((studentReasons) => {
    const [studentId, serializedCodes] = studentReasons.split(':');
    if (!studentId?.trim() || !serializedCodes) {
      return;
    }

    const reasonCodes = serializedCodes
      .split('|')
      .map((code) => Number(code))
      .filter((code) => Number.isInteger(code));
    if (reasonCodes.length > 0) {
      reasonsByStudent[studentId.trim()] = Array.from(new Set(reasonCodes));
    }
  });

  return reasonsByStudent;
};

const groupStudentIdsByReasonCode = (
  reasonsByStudent: Record<string, number[]>
): Array<[number, string[]]> => {
  const studentIdsByReasonCode = new Map<number, string[]>();

  Object.entries(reasonsByStudent).forEach(([studentId, reasonCodes]) => {
    reasonCodes.forEach((reasonCode) => {
      const studentIds = studentIdsByReasonCode.get(reasonCode) ?? [];
      if (!studentIds.includes(studentId)) {
        studentIds.push(studentId);
      }
      studentIdsByReasonCode.set(reasonCode, studentIds);
    });
  });

  return Array.from(studentIdsByReasonCode.entries());
};

const getTourPlanningResult = (notification: Job): TourJobInfo => {
  if (notification.result != null) {
    const resultFromServer: string[] = notification.result.split('#');
    return {
      isError: notification.status === JobStatus.ERROR,
      errorMessage: notification.result,
      isPlan: resultFromServer[0] === 'true',
      numTours: parseInt(resultFromServer[1], 10),
      tourIds: resultFromServer[2],
      numUnassigned: parseInt(resultFromServer[3], 10),
      unassignedStudentIds: resultFromServer[4],
      numUnassignedTypes: parseInt(resultFromServer[5], 10),
      unassignedTypes: resultFromServer[6],
      unassignedStudentReasons: parseUnassignedStudentReasons(resultFromServer[8]),
    };
  }
  return {
    isError: true,
    errorMessage: 'UNKNOWN',
    isPlan: false,
    numTours: 0,
    tourIds: '',
    numUnassigned: 0,
    unassignedStudentIds: '',
    numUnassignedTypes: 0,
    unassignedTypes: '',
    unassignedStudentReasons: {},
  };
};

const getTourTransformationResult = (notification: Job): TourJobInfo => {
  if (notification.status === JobStatus.ERROR || notification.result == null) {
    return {
      isError: true,
      errorMessage: notification.result ?? 'UNKNOWN',
      isPlan: false,
      resultId: '',
      numTours: 0,
      tourIds: '',
      numConflictTours: 0,
      conflictTourIds: '',
    };
  }
  const resultFromServer: string[] = notification.result.split('#');
  return {
    isError: false,
    errorMessage: notification.result,
    isPlan: false,
    resultId: resultFromServer[0],
    numTours: resultFromServer[1] !== '-' ? resultFromServer[1].split(',').length : 0,
    tourIds: resultFromServer[1],
    numConflictTours: parseInt(resultFromServer[2], 10),
    conflictTourIds: resultFromServer[3],
  };
};

const getExcelImportNotificationMessage = (notification: Job): string => {
  if (notification.status === 'FINISHED') {
    return `excelImport.typesSuccess.${notification.payload.split('#')[1].trim()}`;
  }
  if (notification.status === 'ERROR') {
    return `excelImport.typesError.${notification.payload.split('#')[1].trim()}`;
  }
  return notification.payload.split('#')[2];
};

const getExcelImportLink = (notification: Job): string => {
  if (notification.status !== JobStatus.FINISHED) {
    return '#';
  }

  const [, importType, importEntityId] = notification.payload.split('#');
  const schoolId = importEntityId?.trim();
  if (importType?.trim().toUpperCase() !== 'STUDENTS' || !schoolId || schoolId === 'null') {
    return '#';
  }

  return `${PATH_DASHBOARD.student.list}?school_id=${encodeURIComponent(schoolId)}`;
};

const getCheckResultNotificationMessageTransportationNeed = (notification: Job): string => {
  if (notification.status === 'STARTED') {
    return `transportationNeedCheck.transportationNeedCalculation`;
  }
  if (notification.status === 'FINISHED') {
    return `check.finished`;
  }
  return `transportationNeedCheck.transportationNeedCalculationFail`;
};

function getErrorMessageFromResult(tourPlanningResult: TourJobInfo) {
  if (tourPlanningResult.errorMessage === 'ERROR') {
    return `tours.errorNotification.no_tours_planned`;
  }
  return `tours.errorNotification.${tourPlanningResult.errorMessage}`;
}

function getTourLabelFromResult(tourPlanningResult: TourJobInfo) {
  if (tourPlanningResult.numTours > 1) {
    return tourPlanningResult.isPlan ? 'tours.plan.plural' : 'tours.plural';
  }
  return tourPlanningResult.isPlan ? 'tours.plan.singular' : 'tours.singular';
}

function getRoutingMethod(notification: Job) {
  return notification.payload.split('#')[1];
}

function parseExportPayloadForValidFromAndUntil(payload: string) {
  const parts = payload.split('#');
  return {
    from: parts[7],
    until: parts[8],
  };
}

const downloadFile = (url: string, fileName: string) =>
  axios({
    url,
    method: 'GET',
    responseType: 'blob',
  })
    .then((response) => {
      const href = window.URL.createObjectURL(response.data);
      const anchorElement = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'];
      const responseFileName = contentDisposition?.match(/filename="([^"]+)"/)?.[1];

      anchorElement.href = href;
      anchorElement.download = responseFileName ?? fileName;

      document.body.appendChild(anchorElement);
      anchorElement.click();

      document.body.removeChild(anchorElement);
      window.URL.revokeObjectURL(href);
    })
    .catch((error) => {
      console.log('error: ', error);
    });

export function useNotificationContent() {
  const { translate } = useLocales();
  const navigate = useNavigate();

  return (notification: Job) => {
    switch (notification.type) {
      case 'TOUR_PLANNING_STUDENTS':
      case 'TOUR_PLANNING_SPECIAL_TOURS':
      case 'REROUTE_TOURS': {
        const tourPlanningResult = getTourPlanningResult(notification);
        const unassignedReasonGroups = groupStudentIdsByReasonCode(
          tourPlanningResult.unassignedStudentReasons ?? {}
        );
        let linkHref = '#';
        if (
          notification.status === JobStatus.PARTIALLY_FINISHED ||
          notification.status === JobStatus.FINISHED ||
          notification.status === JobStatus.CREDIT_LIMIT_EXCEEDED
        ) {
          const tourOrTourPlan = tourPlanningResult.isPlan
            ? PATH_DASHBOARD.tourPlan
            : PATH_DASHBOARD.tour;
          linkHref = `${tourOrTourPlan.list}?refetch=true&ids=${tourPlanningResult.tourIds}`;
        }

        const jobInformation = (
          <>
            <Typography variant="body1">{notification.name}</Typography>
            {notification.status === JobStatus.STARTED && (
              <Typography variant="body1">
                {`${translate('tours.pendingNotification.base')}`}
              </Typography>
            )}

            {tourPlanningResult.isError && notification.status === JobStatus.FINISHED && (
              <Typography variant="body1">
                {`${translate('tours.errorNotification.base')}  ${translate(
                  getErrorMessageFromResult(tourPlanningResult)
                )}`}
              </Typography>
            )}

            {tourPlanningResult.isError && notification.status === JobStatus.ERROR && (
              <Typography variant="body1">
                {`${translate('tours.errorNotification.base')} ${translate(`tours.errorNotification.status.${notification.result}`)}`}
              </Typography>
            )}

            <Link href={linkHref} underline="none">
              {tourPlanningResult.numTours > 0 && notification.type !== 'REROUTE_TOURS' && (
                <Typography
                  variant="body1"
                  sx={{ color: 'primary.main', ...clickablePlanningResultSx }}
                >
                  {`${tourPlanningResult.numTours} ${translate(
                    getTourLabelFromResult(tourPlanningResult)
                  )} ${translate('tours.generated')}`}
                </Typography>
              )}
            </Link>

            {tourPlanningResult.numTours > 0 && notification.type === 'REROUTE_TOURS' && (
              <Typography variant="body1">
                {`${translate(`tours.rerouted_${getRoutingMethod(notification)}`)}`}
              </Typography>
            )}

            {tourPlanningResult.numTours === 0 &&
              notification.status === JobStatus.CREDIT_LIMIT_EXCEEDED && (
                <Typography variant="body1">{`${translate('credit_limit_exceeded')}`}</Typography>
              )}

            {tourPlanningResult.numTours === 0 && notification.status === JobStatus.FINISHED && (
              <Typography variant="body1">
                {`${translate('tours.not_generated')} ${translate(
                  getTourLabelFromResult(tourPlanningResult)
                )} ${translate('tours.generated')}`}
              </Typography>
            )}

            {tourPlanningResult.numUnassigned !== undefined &&
              tourPlanningResult.numUnassigned > 0 &&
              tourPlanningResult.numUnassignedTypes !== undefined &&
              tourPlanningResult.unassignedTypes !== undefined && (
                <Typography
                  variant="body1"
                  sx={{ color: 'primary.main', ...clickablePlanningResultSx }}
                  onClick={() => {
                    if (tourPlanningResult?.unassignedStudentIds) {
                      navigate(
                        getListPathForType(
                          'STUDENT',
                          tourPlanningResult.unassignedStudentIds.split(',') as string[]
                        )
                      );
                    }
                  }}
                >
                  {`${tourPlanningResult.numUnassigned} ${translate('tours.students_unassigned')}`}
                </Typography>
              )}

            {unassignedReasonGroups.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {unassignedReasonGroups.map(([reasonCode, studentIds]) => {
                  const reasonType = graphHopperReasonTypes[reasonCode];
                  const reasonLabel = reasonType
                    ? String(translate(`tours.unassignedType.${reasonType}`)).trim()
                    : translate('tours.unassignedReasonUnknown', { code: reasonCode });

                  return (
                    <Typography key={reasonCode} variant="caption" sx={{ color: 'text.secondary' }}>
                      {`${studentIds.length}x: ${reasonLabel} (${translate(
                        studentIds.length === 1
                          ? 'tours.errorNotification.details.studentId.singular'
                          : 'tours.errorNotification.details.studentId.plural'
                      )} ${studentIds.join(', ')})`}
                    </Typography>
                  );
                })}
              </Stack>
            )}
          </>
        );

        return {
          jobInformation,
          linkHref,
        };
      }

      case 'TRANSFORM_PLANNED_TOURS': {
        const tourTransformationResult = getTourTransformationResult(notification);
        const hasResultId = Boolean(tourTransformationResult.resultId?.trim());
        const jobInformation = (
          <Box sx={{ color: 'primary.main', cursor: 'pointer' }}>
            <Typography variant="body1">
              {hasResultId ? (
                <Link
                  href={PATH_DASHBOARD.tourPlan.transform(tourTransformationResult.resultId || '')}
                  underline="none"
                >
                  {notification.name}
                </Link>
              ) : (
                notification.name
              )}
              {tourTransformationResult.isError && (
                <Typography variant="body1">
                  {`${translate('tours.errorNotification.base')}  ${translate(
                    getErrorMessageFromResult(tourTransformationResult)
                  )}`}
                </Typography>
              )}
              {!tourTransformationResult.isError && notification.status === JobStatus.STARTED && (
                <Typography variant="body1">{`${translate('waiting_for_result')}`}</Typography>
              )}
              <Link
                href={`${PATH_DASHBOARD.tour.list}?refetch=true&ids=${tourTransformationResult.tourIds}`}
                underline="none"
              >
                {tourTransformationResult.numTours > 0 && (
                  <Typography variant="body1">
                    {`${tourTransformationResult.numTours} ${translate(
                      getTourLabelFromResult(tourTransformationResult)
                    )} ${translate('tours.generated')}`}
                  </Typography>
                )}
              </Link>
              {tourTransformationResult.numTours === 0 &&
                notification.status === JobStatus.FINISHED && (
                  <Typography variant="body1">
                    {`${translate('tours.not_generated')} ${translate(
                      getTourLabelFromResult(tourTransformationResult)
                    )} ${translate('tours.generated')}`}
                  </Typography>
                )}

              {tourTransformationResult.numConflictTours !== undefined &&
                tourTransformationResult.numConflictTours > 0 && (
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', cursor: 'pointer' }}
                    onClick={() => {
                      if (tourTransformationResult?.conflictTourIds) {
                        navigate(
                          getListPathForType(
                            'TOUR_PLAN',
                            tourTransformationResult.conflictTourIds.split(',') as string[]
                          )
                        );
                      }
                    }}
                  >
                    {`${tourTransformationResult.numConflictTours} ${translate('tourTransform.partiallyDone')}`}
                  </Typography>
                )}
            </Typography>
          </Box>
        );

        return {
          jobInformation,
        };
      }

      case 'SCHOOL_BASIC_ACCOUNTING':
      case 'GLOBAL_EXPORT_PDF':
      case 'GLOBAL_EXPORT_EXCEL':
      case 'ZIP_EXPORT_EXCEL':
      case 'TEMPLATED_DOCUMENTS':
      case 'ZIP_EXPORT_PDF': {
        let fileType: string = '';

        if (
          notification.type === 'SCHOOL_BASIC_ACCOUNTING' ||
          notification.type === 'GLOBAL_EXPORT_PDF'
        ) {
          fileType = 'pdf';
        } else if (notification.type === 'GLOBAL_EXPORT_EXCEL') {
          fileType = 'xlsx';
        } else if (
          notification.type === 'ZIP_EXPORT_PDF' ||
          notification.type === 'ZIP_EXPORT_EXCEL'
        ) {
          fileType = 'zip';
        } else if (notification.type === 'TEMPLATED_DOCUMENTS') {
          fileType = 'zip';
        }

        const { from, until } = parseExportPayloadForValidFromAndUntil(notification.payload);
        const fromDate = epochDaysToFormattedDate(from);
        const untilDate = epochDaysToFormattedDate(until);

        const jobInformation = (
          <Typography
            variant="body1"
            sx={{ color: 'primary.main', cursor: 'pointer' }}
            onClick={() =>
              downloadFile(`documents/s/${notification.result}`, `${notification.name}.${fileType}`)
            }
          >
            {notification.name}
            {(fromDate || untilDate) && (
              <Typography component="div" variant="body1">
                {`${translate('jobs.notification.period')}${fromDate || ''}${
                  fromDate && untilDate ? ' - ' : ''
                }${untilDate || ''}`}
              </Typography>
            )}
            {notification.status === JobStatus.ERROR && (
              <Typography component="div" variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {`${translate(`tours.errorNotification.status.${notification.result}`)}`}
              </Typography>
            )}
          </Typography>
        );

        return {
          jobInformation,
        };
      }

      case 'BASIC_REPORT': {
        const jobInformation = (
          <Typography variant="body1">
            {notification.name}
            <Typography component="span" variant="body1">
              &nbsp; {noCase(`${notification.payload}:${notification.result}`)}
            </Typography>
          </Typography>
        );
        return {
          jobInformation,
          linkHref: '#',
        };
      }

      case 'EXCEL_IMPORT': {
        const linkHref = getExcelImportLink(notification);
        const jobInformation = (
          <Typography variant="body1" sx={{ cursor: 'pointer', color: 'primary.main' }}>
            {notification.name}
            &nbsp; {`${translate(getExcelImportNotificationMessage(notification))}`}
          </Typography>
        );
        return {
          jobInformation,
          linkHref,
        };
      }

      case 'TRANSPORTATION_NEED_CHECK': {
        const transportationNeedCheckResult = notification.result ?? '';
        const jobInformation = (
          <Stack
            onClick={() => {
              dispatch(apiSlice.util.invalidateTags(['Student']));
              navigate(
                PATH_DASHBOARD.student.transportationNeedCheck(transportationNeedCheckResult)
              );
            }}
            sx={{ cursor: 'pointer', color: 'primary.main' }}
          >
            <Typography variant="body1">
              {`${translate('transportationNeedCheck.transportationNeedCalculationTitle')}`}
              <Typography component="span" variant="body1">
                &nbsp;{' '}
                {noCase(
                  `${translate(getCheckResultNotificationMessageTransportationNeed(notification))}`
                )}
              </Typography>
            </Typography>
          </Stack>
        );

        return {
          jobInformation,
          linkHref: PATH_DASHBOARD.student.transportationNeedCheck(transportationNeedCheckResult),
        };
      }

      case 'IMPORT_EMPLOYEES_INTO_KEYCLOAK': {
        const getResultImportEmployeesIntoKeycloak = () => {
          const { result } = notification;
          if (result != null) {
            const parts: string[] = result.split('#');
            return ` ${translate('jobs.imported')}: ${parts[0]}  ${translate('jobs.issue')}: ${parts[1]}`;
          }
          return `${translate('waiting_for_result')}`;
        };

        const jobInformation = (
          <Typography variant="body1" sx={{ color: 'primary.main', cursor: 'pointer' }}>
            {`${translate('employeeApp.keycloakImport')}`}
            <Typography component="span" variant="body1">
              &nbsp; {noCase(getResultImportEmployeesIntoKeycloak())}
            </Typography>
          </Typography>
        );
        return {
          jobInformation,
          linkHref: '#',
        };
      }

      default: {
        const { from, until } = parseExportPayloadForValidFromAndUntil(notification.payload);
        const fromDate = epochDaysToFormattedDate(from);
        const untilDate = epochDaysToFormattedDate(until);

        const jobInformation = (
          <Typography variant="body1" sx={{ color: 'primary.main', cursor: 'pointer' }}>
            {notification.name}
            {(fromDate || untilDate) && (
              <Typography component="div" variant="body1">
                {`${translate('jobs.notification.period')}${fromDate || ''}${
                  fromDate && untilDate ? ' - ' : ''
                }${untilDate || ''}`}
              </Typography>
            )}
          </Typography>
        );
        return {
          jobInformation,
          linkHref: '#',
        };
      }
    }
  };
}
